import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Filter, SearchService } from '@docpath/linces-angular-components/common';
import { DpFormModule, DpHeaderModule, DpSearchConfig, DpSearchModule, DpSection, DpSectionIcon, HeaderAction, MoreMenuGroup, SearchBasicInfo, SearchMode, SearchOptionType, SearchRowInfo } from '@docpath/webcomponents';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Constants } from 'src/app/constants/constants';

import { PageHeaderActionsService } from '../../../../../shared/components/header/services/page-header-actions.service';
import { Alias } from '../../../alias/models/alias';
import { AlertRule } from '../../models/alert-rule';
import { AlertRulesService } from '../../services/alert-rules.service';
import { AlertRulesTableService } from '../table/alert-rules-table.service';
import { AlertRulesHeaderService } from './alert-rules-header.service';

@Component({
    selector: 'app-alert-rules-header',
    templateUrl: './alert-rules-header.component.html',
    standalone: true,
    imports: [DpFormModule, DpSearchModule, DpHeaderModule]
})
export class AlertRulesHeaderComponent implements OnChanges, OnInit, OnDestroy {

    @Input() alertRules: AlertRule[] = [];
    @Output() filtersChanged = new EventEmitter<Filter[]>();

    section = new DpSection({ name: 'alert-rules', rows: [], header: 'alert-rules-config.header.title', tooltip: 'alert-rules-config.header.title-tooltip', icon: new DpSectionIcon(faGear) });

    rightActions: HeaderAction[] = [];
    leftActions: HeaderAction[] = [];
    moreMenuGroups: MoreMenuGroup[] = [];

    alias: Alias[] = [];

    showSearch = false;
    searchConfig = new DpSearchConfig({
        resetOnClose: true,
        showMinimizeButton: true
    });

    searchAvailableOptions: SearchRowInfo[] = [
        new SearchRowInfo(true, Constants.CONF_ALERT_RULES_RULE_NAME, undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true),
        new SearchRowInfo(false, Constants.CONF_ALERT_RULES_RULE_DESCRIPTION, undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true),
        new SearchRowInfo(false, Constants.CONF_ALERT_RULES_PRIORITY, undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true)
    ];
    private filters: Filter[] = [];
    private alertRulesChangedSubscription?: Subscription;
    private languageChangeSubscription?: Subscription;

    private readonly alertRulesHeaderService = inject(AlertRulesHeaderService);
    private readonly alertRuleService = inject(AlertRulesService);
    private readonly alertRulesTableService = inject(AlertRulesTableService);
    private readonly pageHeaderActionsService = inject(PageHeaderActionsService);
    private readonly searchService = inject(SearchService);
    private readonly translateService = inject(TranslateService);

    constructor() {
        this.rightActions = this.alertRulesHeaderService.getRightActions(this.alias);
        const searchAction = this.alertRulesHeaderService.getSearchAction(this.rightActions);

        searchAction.action = () => {
            this.showSearch = !this.showSearch;
            this.updateSearchStyle();
        };

        this.updateSearchStyle();
    }

    ngOnInit(): void {

        if (!this.alertRulesChangedSubscription) {
            this.alertRulesChangedSubscription = this.alertRuleService.alertRuleChanged.subscribe(() => {
                this.leftActions = [];
                this.moreMenuGroups = [];
            });
        }

        this.languageChangeSubscription ??= this.translateService.onLangChange.subscribe(() => {
            this.updateSearchStyle();
        });

        this.alertRulesHeaderService.aliasOptionsLoaded.subscribe(result => {
            this.alias = result;
            this.rightActions = this.alertRulesHeaderService.getRightActions(result);
            this.moreMenuGroups = [];
            const searchAction = this.alertRulesHeaderService.getSearchAction(this.rightActions);

            searchAction.action = () => {
                this.showSearch = !this.showSearch;
                this.updateSearchStyle();
            };

            this.updateSearchStyle();
        });

        this.alertRulesHeaderService.loadAliasOptions();
    }

    ngOnDestroy(): void {

        if (this.alertRulesChangedSubscription) {
            this.alertRulesChangedSubscription.unsubscribe();
        }

        this.languageChangeSubscription?.unsubscribe();

    }

    ngOnChanges(changes: SimpleChanges): void {

        // eslint-disable-next-line dot-notation
        if (changes['alertRules']) {
            this.leftActions = this.alertRulesHeaderService.getLeftActions(this.alertRules, this.alias);
            const moreMenuGroupsActions = this.alertRulesHeaderService.getMoreActions(this.alertRules);
            this.moreMenuGroups = moreMenuGroupsActions.length > 0 ? [{ actions: moreMenuGroupsActions }] : [];
        }

    }

    isSearchVisible(): boolean {
        return this.showSearch;
    }

    clearSearch(): void {

        this.filters = [];
        this.filtersChanged.emit(this.filters);
        this.alertRulesTableService.loadAlertRules(this.filters, 1);
        this.leftActions = [];
        this.moreMenuGroups = [];
        this.updateSearchStyle(0);

    }

    sendSearch(currentFilters: Array<SearchBasicInfo>): void {

        this.filters = this.searchService.toFilters(currentFilters);
        this.filtersChanged.emit(this.filters);
        this.alertRulesTableService.loadAlertRules(this.filters, 1);
        this.leftActions = [];
        this.moreMenuGroups = [];
        this.updateSearchStyle();

    }

    hideSearch(): void {
        this.showSearch = false;
    }

    private updateSearchStyle(filtersCount?: number): void {
        const filterKeys = new Set(this.filters.map(filter => filter.key)),
            count = filtersCount ?? filterKeys.size;

        this.pageHeaderActionsService.updateSearchStyle(this.rightActions, count, 'search');
    }

}